/**
 * GuestPreCheckIn — `/checkin/:token`, el pre-check-in que rellena el huésped.
 *
 * Es PÚBLICO: no hay login de admin por medio. El huésped llega desde el
 * enlace que se le manda al confirmar la reserva (§7.2).
 *
 * LÍMITES DE LA VISTA PREVIA, dichos en voz alta:
 *   - el token es una etiqueta legible de demostración, no un secreto. El real
 *     irá hasheado, atado a una reserva, con caducidad y revocación (§7.3.1);
 *   - no se llama a ningún endpoint: lo que se envía vive en la sesión y
 *     desaparece al recargar;
 *   - de este PR solo sale el pre-check-in. El resto del portal del huésped
 *     —mi cuenta, peticiones, limpieza, restaurante, servicios, información,
 *     salida— viene después.
 *
 * Móvil primero: una columna, objetivos de 44px, áreas seguras de iOS arriba y
 * abajo, progreso visible y el mínimo de teclado posible.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CaretLeft, CaretRight, Check, Warning } from "@phosphor-icons/react";

import { useSettingsV2 } from "@/lib/settingsV2Store";
import { usePmsReservations, demoNowISO } from "@/lib/pmsRuntimeStore";
import { useGuestsV2, submitPreCheckIn } from "@/lib/guestsV2Store";
import {
  buildGuestParty, reservationIdForToken, requiredFieldsFor,
  missingFor, hasPersonField, firstIncompleteStep, declaredDocumentState,
  arrivalForMode,
} from "@/lib/guestsV2Selectors";
import {
  ARRIVAL_MODES, arrivalModeOf, TRANSFER_CHOICES, MINIBAR_OPTIONS,
  PRE_CHECKIN_STEPS,
} from "@/data/guestsV2Demo";
import { GUEST_FIELD_LABELS } from "@/data/settingsV2Demo";
import GuestShell, { GuestLinkInvalid } from "@/pages/guest/GuestShell";

/** Alto mínimo de todo lo que se toca. */
const TAP = "min-h-[44px]";
const FIELD = `w-full ${TAP} rounded-lg border px-3 text-base bg-white text-stone-900 placeholder:text-stone-400`;
const FIELD_OK = `${FIELD} border-stone-300`;
const FIELD_BAD = `${FIELD} border-amber-500`;

/** Una fila de persona en blanco no es un huésped incompleto: es una fila de más. */
function rowHasAnything(g) {
  return Boolean(String(g.full_name || "").trim()
    || String(g.nationality || "").trim()
    || String(g.date_of_birth || "").trim()
    || g.document_confirmed);
}

export default function GuestPreCheckIn() {
  const { token } = useParams();
  const settings = useSettingsV2();
  const reservations = usePmsReservations();
  const guestState = useGuestsV2();

  const reservationId = reservationIdForToken(token);
  const reservation = reservations.find((r) => r.reservation_id === reservationId) || null;

  const view = useMemo(() => (reservation
    ? buildGuestParty({ reservation, settings, storeState: guestState })
    : null), [reservation, settings, guestState]);

  const required = useMemo(() => requiredFieldsFor(settings), [settings]);

  const [step, setStep] = useState(0);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [focusTick, setFocusTick] = useState(0);
  const frameRef = useRef(null);

  // El formulario arranca con lo que ya consta: nadie reescribe lo que ya dio.
  const initial = useMemo(() => (view ? {
    guests: view.guests.map((g) => ({
      guest_id: g.guest_id,
      full_name: g.full_name || "",
      nationality: g.nationality || "",
      date_of_birth: g.date_of_birth || "",
      // Lo que ya consta del documento se conserva tal cual: si recepción lo
      // comprobó en persona, reenviar el formulario no lo deshace.
      document: g.document || { status: "not_provided", type: null },
      document_confirmed: (g.document?.status || "not_provided") !== "not_provided",
      primary_guest: Boolean(g.primary_guest),
    })),
    email: view.primary?.email || "",
    phone: view.primary?.phone || "",
    arrival: {
      mode: view.arrival?.mode || "plane",
      reference: view.arrival?.reference || "",
      landing_time: view.arrival?.landing_time || "",
      transfer: view.arrival?.transfer || "",
      estimated_arrival: view.arrival?.estimated_arrival || "",
    },
    preferences: {
      allergies: view.preferences?.allergies || "",
      dietary: view.preferences?.dietary || "",
      minibar: view.preferences?.minibar || "standard",
      scooter_interest: Boolean(view.preferences?.scooter_interest),
      notes: view.preferences?.notes || "",
    },
  } : null), [view]);

  const f = form || initial;
  const set = useCallback((patch) => setForm((prev) => ({ ...(prev || initial), ...patch })), [initial]);
  const setArrival = useCallback((patch) => setForm((prev) => {
    const base = prev || initial;
    return { ...base, arrival: { ...base.arrival, ...patch } };
  }), [initial]);
  const setPrefs = useCallback((patch) => setForm((prev) => {
    const base = prev || initial;
    return { ...base, preferences: { ...base.preferences, ...patch } };
  }), [initial]);
  const setGuest = useCallback((idx, patch) => setForm((prev) => {
    const base = prev || initial;
    const guests = base.guests.map((g, i) => (i === idx ? { ...g, ...patch } : g));
    return { ...base, guests };
  }), [initial]);

  const addGuest = useCallback(() => setForm((prev) => {
    const base = prev || initial;
    return {
      ...base,
      guests: [...base.guests, {
        guest_id: null, full_name: "", nationality: "", date_of_birth: "",
        document: { status: "not_provided", type: null },
        document_confirmed: false, primary_guest: false,
      }],
    };
  }), [initial]);

  /**
   * Las personas tal como se enviarían ahora mismo. Es lo que se valida: no
   * lo que ya había guardado, sino lo que el huésped tiene escrito delante.
   */
  const draftGuests = useMemo(() => (f ? f.guests.filter(rowHasAnything).map((g) => ({
    guest_id: g.guest_id,
    reservation_id: reservationId,
    primary_guest: Boolean(g.primary_guest),
    full_name: String(g.full_name || "").trim(),
    nationality: g.nationality || null,
    date_of_birth: g.date_of_birth || null,
    // Marcar la casilla NO sube un archivo: lo máximo que puede pasar aquí es
    // que el huésped CONFIRME que trae el documento (§7.3.5).
    document: declaredDocumentState(g.document, g.document_confirmed),
    ...(g.primary_guest ? { email: f.email || null, phone: f.phone || null } : {}),
  })) : []), [f, reservationId]);

  /**
   * Lo que falta para poder enviar, con las MISMAS reglas que usa el equipo
   * (`missingFor`): lo obligatorio lo dice Ajustes y las preferencias no
   * cuentan. Si esto no estuviera, alguien podría pasar los cuatro pasos en
   * blanco y ver "Todo recibido" sin haber dado un solo dato.
   */
  const missing = useMemo(() => {
    if (!f) return [];
    const out = missingFor({ guests: draftGuests, required, arrival: f.arrival });
    if (draftGuests.length === 0) {
      out.unshift({ scope: "person", field: "full_name", guest_id: null, guest_name: null });
    }
    return out;
  }, [f, draftGuests, required]);

  const partyErrors = useMemo(
    () => (showErrors ? missing.filter((m) => m.scope === "party").map((m) => m.field) : []),
    [showErrors, missing],
  );

  /** Lo que le falta a una fila concreta, con la regla del modelo, no otra. */
  const rowErrors = useCallback((row) => {
    if (!showErrors || !rowHasAnything(row)) return [];
    const draft = {
      full_name: String(row.full_name || "").trim(),
      nationality: row.nationality || null,
      date_of_birth: row.date_of_birth || null,
      document: declaredDocumentState(row.document, row.document_confirmed),
    };
    return required.person.filter((field) => !hasPersonField(draft, field));
  }, [showErrors, required]);

  // Al fallar el envío, el foco va al primer campo que lo impide. Un aviso sin
  // sitio al que ir es un callejón sin salida, y en móvil lo que falta puede
  // estar dos pantallas atrás.
  useEffect(() => {
    if (!focusTick) return;
    const el = frameRef.current?.querySelector(
      '[data-invalid="true"] input, [data-invalid="true"] textarea',
    );
    if (el && typeof el.focus === "function") el.focus();
  }, [focusTick]);

  const onSubmit = useCallback(() => {
    if (!reservationId || !f) return;

    if (missing.length > 0) {
      setShowErrors(true);
      const target = firstIncompleteStep(missing);
      const idx = PRE_CHECKIN_STEPS.findIndex((s) => s.key === target);
      if (idx >= 0) setStep(idx);
      setFocusTick((n) => n + 1);
      return;                                   // NO se envía y NO se dice que sí
    }

    submitPreCheckIn(reservationId, {
      guests: draftGuests,
      arrival: { ...f.arrival, transfer: f.arrival.transfer || null },
      preferences: f.preferences,
      at: demoNowISO(),
    });
    setSent(true);
  }, [reservationId, f, missing, draftGuests]);

  if (!reservationId || !reservation) return <GuestLinkInvalid />;

  if (sent) {
    return (
      <GuestShell token={token} area="checkin" title="Antes de llegar" subtitle={settings.property.name}>
        <div className="text-center py-10" data-testid="guest-precheckin-done">
          <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-4">
            <Check size={26} weight="bold" />
          </div>
          <h1 className="font-serif-dv text-2xl text-stone-900">Todo recibido</h1>
          <p className="text-sm text-stone-600 mt-2 max-w-sm mx-auto">
            Gracias. Tenemos tus datos para preparar la llegada
            {view?.arrival?.estimated_arrival ? ` sobre las ${view.arrival.estimated_arrival}` : ""}.
            Si cambia algo, vuelve a este mismo enlace.
          </p>
        </div>
      </GuestShell>
    );
  }

  const stepKey = PRE_CHECKIN_STEPS[step].key;
  const last = step === PRE_CHECKIN_STEPS.length - 1;
  const missingLabels = [...new Set(missing.map((m) => GUEST_FIELD_LABELS[m.field] || m.field))];

  return (
    <GuestShell token={token} area="checkin" title="Antes de llegar" subtitle={settings.property.name}>
      <div ref={frameRef}>
      <p className="text-sm text-stone-500 -mt-4 mb-5">
        {reservation.check_in} → {reservation.check_out}
        {" · "}entrada a partir de las {settings.property.check_in_time}
      </p>

      {/* Progreso: cuántos pasos hay y en cuál va. */}
      <ol className="flex items-center gap-1.5 mb-5" data-testid="guest-progress">
        {PRE_CHECKIN_STEPS.map((s, i) => (
          <li key={s.key} className="flex-1">
            <div
              className={`h-1 rounded-full ${i <= step ? "bg-stone-900" : "bg-stone-200"}`}
              aria-current={i === step ? "step" : undefined}
            />
            <span className={`block text-[10px] mt-1 ${i === step ? "text-stone-900" : "text-stone-400"}`}>
              {s.label}
            </span>
          </li>
        ))}
      </ol>

      {/* Lo que impide enviar, dicho una vez y en el paso donde se arregla. */}
      {showErrors && missing.length > 0 && (
        <div
          role="alert"
          data-testid="guest-validation"
          className="mb-4 rounded-lg border border-amber-400 bg-amber-50 px-3 py-2.5"
        >
          <p className="text-sm text-amber-900 flex items-start gap-1.5">
            <Warning size={15} className="mt-0.5 shrink-0" />
            <span>
              {missing.length === 1
                ? "Falta 1 dato para poder enviarlo."
                : `Faltan ${missing.length} datos para poder enviarlo.`}
            </span>
          </p>
          <p className="text-xs text-amber-800 mt-1" data-testid="guest-validation-fields">
            {missingLabels.join(" · ")}
          </p>
          <p className="text-[11px] text-amber-700 mt-1">
            Las preferencias no hacen falta: esas puedes dejarlas en blanco.
          </p>
        </div>
      )}

      {stepKey === "guests" && (
        <StepGuests
          f={f} required={required} setGuest={setGuest} set={set} addGuest={addGuest}
          rowErrors={rowErrors} partyErrors={partyErrors}
        />
      )}
      {stepKey === "arrival" && (
        <StepArrival f={f} setArrival={setArrival} partyErrors={partyErrors} />
      )}
      {stepKey === "preferences" && <StepPreferences f={f} setPrefs={setPrefs} />}
      {stepKey === "review" && <StepReview f={f} view={view} />}

      <div className="flex items-center gap-2 mt-6">
        {step > 0 && (
          <button
            type="button"
            onClick={() => setStep((s) => s - 1)}
            data-testid="guest-back"
            className={`${TAP} px-4 rounded-full border border-stone-300 text-sm text-stone-600 inline-flex items-center gap-1`}
          >
            <CaretLeft size={13} /> Atrás
          </button>
        )}
        {!last ? (
          <button
            type="button"
            onClick={() => setStep((s) => s + 1)}
            data-testid="guest-next"
            className={`${TAP} flex-1 px-4 rounded-full bg-stone-900 text-white text-sm inline-flex items-center justify-center gap-1`}
          >
            Continuar <CaretRight size={13} weight="bold" />
          </button>
        ) : (
          <button
            type="button"
            onClick={onSubmit}
            data-testid="guest-submit"
            className={`${TAP} flex-1 px-4 rounded-full bg-stone-900 text-white text-sm inline-flex items-center justify-center gap-1.5`}
          >
            <Check size={14} weight="bold" /> Enviar
          </button>
        )}
      </div>

      </div>
    </GuestShell>
  );
}

/* -------------------------------------------------------------------------
 * Marco — una columna, áreas seguras arriba y abajo
 * ---------------------------------------------------------------------- */

function Field({ label, hint, children, testId, invalid = false }) {
  return (
    <label className="block mb-3" data-testid={testId} data-invalid={invalid ? "true" : undefined}>
      <span className={`block text-xs mb-1 ${invalid ? "text-amber-800" : "text-stone-600"}`}>
        {label}
        {invalid && <span className="ml-1">· falta</span>}
      </span>
      {children}
      {hint && <span className="block text-[11px] text-stone-400 mt-1">{hint}</span>}
    </label>
  );
}

/** Botones grandes en lugar de un desplegable: menos teclado, más dedo. */
function ChoiceRow({ options, value, onChange, testId }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={testId}>
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            aria-pressed={active}
            data-testid={`${testId}-${o.value}`}
            className={`${TAP} px-3 rounded-lg border text-sm text-left ${active
              ? "border-stone-900 bg-stone-900 text-white"
              : "border-stone-300 bg-white text-stone-700"}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Paso 1 — quién viene
 * ---------------------------------------------------------------------- */

function StepGuests({ f, required, setGuest, set, addGuest, rowErrors, partyErrors }) {
  const needs = (field) => required.person.includes(field);
  return (
    <section data-testid="guest-step-guests">
      {f.guests.map((g, i) => {
        const bad = rowErrors(g);
        const wrong = (field) => bad.includes(field);
        return (
          <div key={g.guest_id || `new-${i}`} className="mb-4 pb-4 border-b border-stone-200 last:border-0">
            <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-2">
              {i === 0 ? "Huésped principal" : `Acompañante ${i}`}
            </div>
            <Field label="Nombre y apellidos" testId={`guest-name-${i}`} invalid={wrong("full_name")}>
              <input
                className={wrong("full_name") ? FIELD_BAD : FIELD_OK}
                value={g.full_name}
                onChange={(e) => setGuest(i, { full_name: e.target.value })}
                autoComplete={i === 0 ? "name" : "off"}
              />
            </Field>
            {needs("nationality") && (
              <Field
                label={GUEST_FIELD_LABELS.nationality}
                testId={`guest-nationality-${i}`}
                invalid={wrong("nationality")}
              >
                <input
                  className={wrong("nationality") ? FIELD_BAD : FIELD_OK}
                  value={g.nationality}
                  onChange={(e) => setGuest(i, { nationality: e.target.value })}
                />
              </Field>
            )}
            {needs("date_of_birth") && (
              <Field
                label={GUEST_FIELD_LABELS.date_of_birth}
                hint="Nos la pide inmigración para el registro de extranjeros."
                testId={`guest-dob-${i}`}
                invalid={wrong("date_of_birth")}
              >
                <input
                  type="date" className={wrong("date_of_birth") ? FIELD_BAD : FIELD_OK}
                  value={g.date_of_birth}
                  onChange={(e) => setGuest(i, { date_of_birth: e.target.value })}
                />
              </Field>
            )}
            {needs("passport") && (
              // No hay subida de ficheros: esta casilla es una CONFIRMACIÓN, y
              // se dice así. "Adjunto mi pasaporte" haría creer a recepción que
              // hay un archivo esperándole en alguna pantalla.
              <label
                className={`${TAP} flex items-center gap-3 px-3 rounded-lg border bg-white ${
                  wrong("passport") ? "border-amber-500" : "border-stone-300"}`}
                data-testid={`guest-document-${i}`}
                data-invalid={wrong("passport") ? "true" : undefined}
              >
                <input
                  type="checkbox"
                  className="w-5 h-5"
                  checked={g.document_confirmed}
                  onChange={(e) => setGuest(i, { document_confirmed: e.target.checked })}
                />
                <span className="text-sm text-stone-700">
                  Confirmo que llevo mi {GUEST_FIELD_LABELS.passport.toLowerCase()} y
                  lo enseñaré a la llegada
                </span>
              </label>
            )}
          </div>
        );
      })}

      <button
        type="button"
        onClick={addGuest}
        data-testid="guest-add"
        className={`${TAP} w-full rounded-lg border border-dashed border-stone-300 text-sm text-stone-600`}
      >
        + Añadir acompañante
      </button>

      <div className="mt-5 pt-4 border-t border-stone-200">
        <Field label="Email de contacto" testId="guest-email" invalid={partyErrors.includes("email")}>
          <input
            type="email" inputMode="email" autoComplete="email"
            className={partyErrors.includes("email") ? FIELD_BAD : FIELD_OK}
            value={f.email} onChange={(e) => set({ email: e.target.value })}
          />
        </Field>
        <Field
          label="Teléfono" hint="Para avisarte el día de la llegada."
          testId="guest-phone" invalid={partyErrors.includes("phone")}
        >
          <input
            type="tel" inputMode="tel" autoComplete="tel"
            className={partyErrors.includes("phone") ? FIELD_BAD : FIELD_OK}
            value={f.phone} onChange={(e) => set({ phone: e.target.value })}
          />
        </Field>
      </div>

      <p className="text-[11px] text-stone-400 mt-2" data-testid="guest-document-privacy">
        Aquí no se sube ningún archivo: solo queda constancia de que confirmas
        que lo llevas, y se comprueba a la llegada. Ni el número ni la imagen de
        tu documento se enseñan en las pantallas del equipo.
      </p>
    </section>
  );
}

/* -------------------------------------------------------------------------
 * Paso 2 — cómo llega (campos condicionales)
 * ---------------------------------------------------------------------- */

function StepArrival({ f, setArrival, partyErrors }) {
  const mode = arrivalModeOf(f.arrival.mode);
  const badTime = partyErrors.includes("arrival_time");
  return (
    <section data-testid="guest-step-arrival">
      <div className="mb-4">
        <span className="block text-xs text-stone-600 mb-1.5">¿Cómo llegas?</span>
        <ChoiceRow
          options={ARRIVAL_MODES}
          value={f.arrival.mode}
          // Cambiar de forma de llegada limpia lo que deja de tener sentido: un
          // número de vuelo no sobrevive a "vengo en barco", y un traslado desde
          // el aeropuerto no sobrevive a "llego por mi cuenta".
          onChange={(v) => setArrival(arrivalForMode(f.arrival, v))}
          testId="guest-arrival-mode"
        />
      </div>

      {/* Condicional: el número solo tiene sentido si viene en avión o barco. */}
      {mode.ref_label && (
        <Field
          label={mode.ref_label}
          testId="guest-arrival-reference"
          invalid={partyErrors.includes("flight_number")}
        >
          <input
            className={partyErrors.includes("flight_number") ? FIELD_BAD : FIELD_OK}
            placeholder={mode.ref_placeholder}
            value={f.arrival.reference}
            onChange={(e) => setArrival({ reference: e.target.value })}
          />
        </Field>
      )}
      {mode.time_label && (
        <Field label={mode.time_label} testId="guest-arrival-landing">
          <input
            type="time" className={FIELD_OK} value={f.arrival.landing_time}
            onChange={(e) => setArrival({ landing_time: e.target.value })}
          />
        </Field>
      )}

      {/*
        El traslado solo se pregunta cuando hay de dónde recoger a alguien. A
        quien llega por su cuenta no se le ofrece: el catálogo no tiene ningún
        traslado genérico, así que preguntarlo sería ofrecer un servicio que no
        existe — y dejaría a recepción con un "sin indicar" que perseguir.
      */}
      {mode.transfer_label && (
        <div className="mb-4">
          <span className="block text-xs text-stone-600 mb-1.5" data-testid="guest-transfer-label">
            {mode.transfer_label}
          </span>
          <ChoiceRow
            options={TRANSFER_CHOICES}
            value={f.arrival.transfer}
            onChange={(v) => setArrival({ transfer: v })}
            testId="guest-transfer"
          />
        </div>
      )}

      <Field
        label="¿A qué hora calculas llegar a la villa?"
        hint="Aproximada nos vale. Sirve para tener la villa lista a tiempo."
        testId="guest-eta"
        invalid={badTime}
      >
        <input
          type="time" className={badTime ? FIELD_BAD : FIELD_OK}
          value={f.arrival.estimated_arrival}
          onChange={(e) => setArrival({ estimated_arrival: e.target.value })}
        />
      </Field>
    </section>
  );
}

/* -------------------------------------------------------------------------
 * Paso 3 — preferencias (todas opcionales)
 * ---------------------------------------------------------------------- */

function StepPreferences({ f, setPrefs }) {
  return (
    <section data-testid="guest-step-preferences">
      <p className="text-xs text-stone-500 mb-4" data-testid="guest-optional-note">
        Todo lo de esta página es opcional. Si lo dejas en blanco, tu
        pre-check-in sigue estando completo.
      </p>

      <Field label="Alergias o intolerancias" testId="guest-allergies">
        <textarea
          rows={2} className={`${FIELD_OK} py-2`} value={f.preferences.allergies}
          onChange={(e) => setPrefs({ allergies: e.target.value })}
          placeholder="Frutos secos, lactosa…"
        />
      </Field>
      <Field label="Dieta" testId="guest-dietary">
        <textarea
          rows={2} className={`${FIELD_OK} py-2`} value={f.preferences.dietary}
          onChange={(e) => setPrefs({ dietary: e.target.value })}
          placeholder="Vegetariano, sin cerdo…"
        />
      </Field>

      <div className="mb-4">
        <span className="block text-xs text-stone-600 mb-1.5">Minibar</span>
        <ChoiceRow
          options={MINIBAR_OPTIONS}
          value={f.preferences.minibar}
          onChange={(v) => setPrefs({ minibar: v })}
          testId="guest-minibar"
        />
        <span className="block text-[11px] text-stone-400 mt-1" data-testid="guest-minibar-note">
          Solo para preparar la nevera. No te preguntamos el motivo.
        </span>
      </div>

      <label
        className={`${TAP} flex items-center gap-3 px-3 rounded-lg border border-stone-300 bg-white mb-3`}
        data-testid="guest-scooter"
      >
        <input
          type="checkbox" className="w-5 h-5"
          checked={f.preferences.scooter_interest}
          onChange={(e) => setPrefs({ scooter_interest: e.target.checked })}
        />
        <span className="text-sm text-stone-700">Me interesa alquilar una scooter</span>
      </label>

      <Field label="Algo más que debamos saber" testId="guest-notes">
        <textarea
          rows={3} className={`${FIELD_OK} py-2`} value={f.preferences.notes}
          onChange={(e) => setPrefs({ notes: e.target.value })}
        />
      </Field>
    </section>
  );
}

/* -------------------------------------------------------------------------
 * Paso 4 — repaso
 * ---------------------------------------------------------------------- */

function StepReview({ f, view }) {
  const named = f.guests.filter((g) => String(g.full_name || "").trim());
  const mode = arrivalModeOf(f.arrival.mode);
  const transfer = TRANSFER_CHOICES.find((t) => t.value === f.arrival.transfer);
  return (
    <section data-testid="guest-step-review" className="text-sm">
      <Line label="Huéspedes" value={named.map((g) => g.full_name).join(", ") || "—"} />
      <Line label="Contacto" value={[f.email, f.phone].filter(Boolean).join(" · ") || "No disponible"} />
      <Line label="Llegada" value={[
        mode.label, f.arrival.reference || null,
        f.arrival.estimated_arrival ? `sobre las ${f.arrival.estimated_arrival}` : null,
      ].filter(Boolean).join(" · ")} />
      {/* Si no se le preguntó por el traslado, tampoco se le repasa. */}
      {mode.transfer_label && (
        <Line label="Traslado" value={transfer?.label || "Sin indicar"} />
      )}
      <Line label="Minibar" value={f.preferences.minibar === "alcohol_free" ? "Sin alcohol" : "Estándar"} />
      {f.preferences.allergies && <Line label="Alergias" value={f.preferences.allergies} />}
      {view?.immigration?.tracked && (
        <p className="text-[11px] text-stone-400 mt-4" data-testid="guest-immigration-note">
          Con estos datos preparamos el registro de extranjeros que exige la
          normativa local. Lo presenta la propiedad, no tú.
        </p>
      )}
    </section>
  );
}

function Line({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-stone-200 last:border-0">
      <span className="text-stone-500 shrink-0">{label}</span>
      <span className="text-stone-900 text-right">{value}</span>
    </div>
  );
}
